import { useState } from 'react';

import { cn } from '@/lib/utils';
import { formatTime, initials } from '@/modules/debug/linkedin/format';
import {
  filterThreads,
  replyTargetJob,
  senderKindLabel,
} from '@/modules/debug/linkedin/linkedInDebug';
import type { DebugLinkedInThread } from '@/modules/debug/linkedin/types';
import { Avatar, AvatarFallback } from '@/modules/ui/components/avatar/Avatar';
import { Input } from '@/modules/ui/components/input/Input';
import Loader from '@/modules/ui/components/loader/Loader';

interface ThreadListProps {
  threads: DebugLinkedInThread[];
  selectedThreadId: string | null;
  onSelect: (id: string) => void;
  loading: boolean;
}

export function ThreadList({
  threads,
  selectedThreadId,
  onSelect,
  loading,
}: ThreadListProps) {
  const [query, setQuery] = useState('');
  const visibleThreads = filterThreads(threads, query);

  return (
    <>
      <div className="border-b px-4 py-3">
        <p className="mb-2 text-sm font-semibold">Conversations</p>
        <Input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search candidate or job…"
          aria-label="Search conversations"
          autoFocus
        />
      </div>
      {loading ? (
        <div className="flex h-40 items-center justify-center">
          <Loader />
        </div>
      ) : visibleThreads.length === 0 ? (
        <div className="text-muted-foreground p-6 text-center text-sm">
          {threads.length === 0
            ? 'No conversations for this account yet.'
            : 'No conversations match your search.'}
        </div>
      ) : (
        <ul className="divide-y">
          {visibleThreads.map((thread) => {
            const isActive = thread.threadId === selectedThreadId;
            const replyJob = replyTargetJob(thread.candidateJobs);
            return (
              <li key={thread.threadId}>
                <button
                  type="button"
                  onClick={() => onSelect(thread.threadId)}
                  className={cn(
                    'flex w-full items-start gap-3 px-4 py-3 text-left transition-colors',
                    isActive ? 'bg-primary/5' : 'hover:bg-muted/60',
                  )}
                >
                  <Avatar className="size-10 bg-[#0a66c2]">
                    <AvatarFallback className="bg-[#0a66c2] text-white">
                      {initials(thread.candidateName)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <p className="truncate text-sm font-semibold">
                        {thread.candidateName}
                      </p>
                      <span className="text-muted-foreground shrink-0 text-xs">
                        {formatTime(thread.lastMessageAt)}
                      </span>
                    </div>
                    {thread.candidateHeadline && (
                      <p className="text-muted-foreground truncate text-xs">
                        {thread.candidateHeadline}
                      </p>
                    )}
                    {replyJob && (
                      <p className="mt-0.5 truncate text-xs font-medium text-[#0a66c2]/80">
                        {replyJob.jobTitle}
                        {thread.candidateJobs.length > 1 &&
                          ` +${thread.candidateJobs.length - 1}`}
                      </p>
                    )}
                    {thread.senderKind && (
                      <p className="text-muted-foreground text-[11px]">
                        {senderKindLabel(thread.senderKind)}
                      </p>
                    )}
                    <p className="text-muted-foreground mt-1 line-clamp-2 text-xs">
                      {thread.lastMessagePreview ?? '—'}
                    </p>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
}
